import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTranslatedUI } from '../hooks/useTranslatedUI';
import { translateBatch } from '../services/translationService';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Check, Crown, Shield, Phone, FileText, 
  HelpCircle, ArrowRight, Star, Lock, Sparkles,
  AlertCircle, X, CreditCard, CheckCircle, Loader2
} from 'lucide-react';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const SUB_STRINGS = {
  paymentSuccess: "Payment Successful!",
  paymentSuccessDesc: "Your premium subscription has been activated. You now have access to all premium features.",
  explorePremium: "Explore Premium Schemes",
  eligibleFor: "You are eligible for",
  schemesExcl: "schemes!",
  basedOnProfile: "Based on your profile",
  category: "category",
  freeAccess: "Free Access",
  premiumAccess: "Premium Access",
  premiumAssistance: "Premium Assistance",
  getExpertHelp: "Get Expert Help with Your Applications",
  expertDesc: "Our team of experts will guide you through the entire application process, from eligibility checking to document submission.",
  bestValue: "Best Value",
  only: "only",
  processing: "Processing...",
  subscribeNow: "Subscribe Now",
  securePayment: "Secure payment via Razorpay",
  exploreFreeNow: "Explore Free Schemes Now!",
  exploreFreeDesc: "You can also explore and apply for many free government schemes.",
  exploreFreeBtn: "Explore Free Schemes",
  importantInfo: "Important Information",
  info1: "We provide assistance services only, NOT government benefits directly.",
  info2: "Government authorities make final decisions on scheme approvals.",
  info3: "Subscription fees are non-refundable once services are activated.",
  info4: "For urgent assistance, contact us via WhatsApp: +91 91876 36397",
  eligibilityCheck: "Eligibility Check",
  eligibilityDesc: "We verify your eligibility for all applicable government schemes",
  formFilling: "Form Filling",
  formFillingDesc: "Expert assistance with application forms and document preparation",
  phoneSupport: "Phone Support",
  phoneSupportDesc: "Dedicated support team available to guide you through the process",
};

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const ui = useTranslatedUI(SUB_STRINGS);
  
  const [plans, setPlans] = useState([]);
  const [rawPlans, setRawPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [eligibilityCount, setEligibilityCount] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    fetchData();
    loadRazorpayScript();
  }, [user]);

  // Translate plan features when language changes
  useEffect(() => {
    if (rawPlans.length === 0) return;
    if (language === 'en') {
      setPlans(rawPlans);
      return;
    }
    const allFeatures = rawPlans.flatMap(p => p.features);
    translateBatch(allFeatures, language).then(translated => {
      let idx = 0;
      const translatedPlans = rawPlans.map(p => ({
        ...p,
        features: p.features.map(() => translated[idx++] || p.features[idx - 1])
      }));
      setPlans(translatedPlans);
    }).catch(() => setPlans(rawPlans));
  }, [language, rawPlans]);

  const loadRazorpayScript = () => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch subscription plans
      const plansRes = await axios.get(`${API_URL}/api/subscription/plans`);
      setRawPlans(plansRes.data.plans);
      
      if (user) {
        // Fetch eligibility count
        const eligibilityRes = await axios.get(
          `${API_URL}/api/subscription/eligibility-count?user_id=${user.id}`
        );
        setEligibilityCount(eligibilityRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setPaymentLoading(true);
    setPaymentError('');

    try {
      // Create order on backend
      const orderRes = await axios.post(`${API_URL}/api/payments/create-order`, {
        plan_id: planId,
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        user_phone: user.phone
      });

      const orderData = orderRes.data;

      if (!orderData.success) {
        throw new Error(orderData.detail || 'Failed to create order');
      }

      // Configure Razorpay options
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Know My Schemes',
        description: orderData.description,
        order_id: orderData.order_id,
        prefill: {
          name: orderData.prefill.name,
          email: orderData.prefill.email,
          contact: orderData.prefill.contact
        },
        theme: {
          color: '#16a34a'
        },
        handler: async function(response) {
          // Verify payment on backend
          try {
            const verifyRes = await axios.post(`${API_URL}/api/payments/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: user.id,
              plan_id: planId
            });

            if (verifyRes.data.success) {
              setPaymentSuccess(true);
              // Refresh user data after successful payment
              setTimeout(() => {
                window.location.reload();
              }, 3000);
            } else {
              setPaymentError('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Verification error:', error);
            setPaymentError('Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: function() {
            setPaymentLoading(false);
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function(response) {
        setPaymentError(`Payment failed: ${response.error.description}`);
        setPaymentLoading(false);
      });
      razorpay.open();

    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
      setPaymentLoading(false);
    }
  };

  // Success Modal
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{ui.paymentSuccess}</h2>
          <p className="text-gray-600 mb-6">
            {ui.paymentSuccessDesc}
          </p>
          <Button 
            onClick={() => navigate('/schemes')}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {ui.explorePremium}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Eligibility Banner */}
        {eligibilityCount && (
          <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">
                    {ui.eligibleFor} {eligibilityCount.total_eligible} {ui.schemesExcl}
                  </h2>
                  <p className="text-purple-100">
                    {ui.basedOnProfile} ({eligibilityCount.user_caste} {ui.category})
                  </p>
                </div>
              </div>
              <div className="flex gap-4 text-center">
                <div className="bg-white/20 rounded-lg px-4 py-2">
                  <div className="text-2xl font-bold">{eligibilityCount.free_schemes}</div>
                  <div className="text-xs text-purple-100">{ui.freeAccess}</div>
                </div>
                <div className="bg-white/20 rounded-lg px-4 py-2">
                  <div className="text-2xl font-bold">{eligibilityCount.premium_schemes}</div>
                  <div className="text-xs text-purple-100">{ui.premiumAccess}</div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Page Header */}
        <div className="text-center mb-10">
          <Badge className="bg-orange-100 text-orange-700 mb-4">
            <Crown className="w-3 h-3 mr-1" />
            {ui.premiumAssistance}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {ui.getExpertHelp}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {ui.expertDesc}
          </p>
        </div>

        {/* Payment Error */}
        {paymentError && (
          <Card className="bg-red-50 border-red-200 p-4 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{paymentError}</p>
              <button 
                onClick={() => setPaymentError('')}
                className="ml-auto p-1 hover:bg-red-100 rounded"
              >
                <X className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </Card>
        )}

        {/* Subscription Plans */}
        <div className="flex justify-center mb-10">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`p-8 relative max-w-md w-full ${
                plan.recommended 
                  ? 'border-2 border-green-500 shadow-xl' 
                  : 'border border-gray-200'
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-green-600 text-white">
                    <Star className="w-3 h-3 mr-1" />
                    {ui.bestValue}
                  </Badge>
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                <p className="text-sm text-gray-500">{plan.duration}</p>
                <div className="mt-4">
                  {plan.original_price && (
                    <div className="mb-2">
                      <span className="text-xl text-gray-400 line-through">{plan.original_price}</span>
                      {plan.discount && (
                        <span className="ml-2 px-3 py-1 bg-red-100 text-red-600 text-sm font-bold rounded-full">{plan.discount}</span>
                      )}
                    </div>
                  )}
                  <span className="text-5xl font-bold text-gray-900">{plan.price_display}</span>
                  <span className="text-gray-500 ml-2">{ui.only}</span>
                </div>
              </div>
              
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => handlePayment(plan.id)}
                disabled={paymentLoading}
                className="w-full py-4 bg-green-600 hover:bg-green-700 text-white font-semibold text-lg"
                data-testid="subscribe-btn"
              >
                {paymentLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {ui.processing}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    {ui.subscribeNow}
                  </span>
                )}
              </Button>

              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Lock className="w-4 h-4" />
                <span>{ui.securePayment}</span>
              </div>
            </Card>
          ))}
        </div>

        {/* Free Schemes CTA */}
        <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold mb-2">{ui.exploreFreeNow}</h3>
              <p className="text-green-100">
                {ui.exploreFreeDesc}
              </p>
            </div>
            <Button 
              onClick={() => navigate('/schemes')}
              className="bg-white text-green-700 hover:bg-green-50 px-6"
              data-testid="explore-free-schemes-btn"
            >
              {ui.exploreFreeBtn}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>

        {/* Important Information */}
        <Card className="bg-amber-50 border-amber-200 p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-2">{ui.importantInfo}</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• {ui.info1}</li>
                <li>• {ui.info2}</li>
                <li>• {ui.info3}</li>
                <li>• {ui.info4}</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* What You Get Section */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{ui.eligibilityCheck}</h3>
            <p className="text-sm text-gray-600">
              {ui.eligibilityDesc}
            </p>
          </Card>
          
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{ui.formFilling}</h3>
            <p className="text-sm text-gray-600">
              {ui.formFillingDesc}
            </p>
          </Card>
          
          <Card className="p-6 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{ui.phoneSupport}</h3>
            <p className="text-sm text-gray-600">
              {ui.phoneSupportDesc}
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
